import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SearchItemLabel, usePopup } from '..'
import { intl } from '../../../../../i18n'
import { useProjectPrivateContext } from '../../../../context/projectContext'
import { useWbsItemRepository } from '../../../../../services/adaptors/wbsItemRepositoryAdaptor'
import { WbsItemType } from '../../../../../domain/entity/WbsItemEntity'
import { SelectOption } from '../../../../model/selectOption'
import ProcessScopeSearchSelectPopper from '../../../../features/ProcessScopeSearchSelectPopper/ProcessScopeSearchSelectPopper'
import { isDescendantOf } from '../../../../features/ProcessScopeSearchSelectPopper/treeUtils'

type ProcessScopeConditionItemLabelProps = {
  value: string[] | undefined
  onChange: (newValue: string[] | undefined) => void
  onClose: () => void
  search: () => void
  isLoading: boolean
}

export const ProcessScopeConditionItemLabel = ({
  value,
  onChange,
  onClose,
  search,
  isLoading,
}: ProcessScopeConditionItemLabelProps) => {
  const { ref, open, openPopup, closePopup } = usePopup()
  const { projectUuid } = useProjectPrivateContext()
  const { fetchListAsReferencedEntity } = useWbsItemRepository()
  const [options, setOptions] = useState<SelectOption[]>([])
  const [parentMap, setParentMap] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!projectUuid) return
    fetchListAsReferencedEntity(projectUuid, [
      WbsItemType.WORKGROUP,
      WbsItemType.PROCESS,
      WbsItemType.DELIVERABLE_LIST,
    ]).then(results => {
      setOptions(
        results.map(item => ({
          value: item.uuid,
          name: item.name,
          iconUrl: item.iconUrl,
          tooltip: item.path,
        }))
      )
      setParentMap(
        Object.fromEntries(
          results
            .filter(item => item.parentUuid)
            .map(item => [item.uuid, item.parentUuid!])
        )
      )
    })
  }, [projectUuid, fetchListAsReferencedEntity])

  const selectedValues = useMemo(() => new Set(value || []), [value])

  const valueNames = useMemo(() => {
    return options
      .filter(op => (value || []).includes(op.value))
      .map(op => op.name)
  }, [value, options])

  const onClear = useCallback(() => {
    onChange(undefined)
    onClose()
  }, [onChange, onClose])

  const onClosePopup = useCallback(() => {
    search()
    closePopup()
  }, [search, closePopup])

  const valueRef = useRef(value)
  valueRef.current = value

  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const handleToggleItem = useCallback(
    (itemValue: string) => {
      const current = new Set(valueRef.current || [])
      if (current.has(itemValue)) {
        current.delete(itemValue)
      } else {
        current.add(itemValue)
        // 親を選択した場合、配下の選択を解除する
        for (const selected of [...current]) {
          if (
            selected !== itemValue &&
            isDescendantOf(selected, itemValue, parentMap)
          ) {
            current.delete(selected)
          }
        }
      }
      const newValue = Array.from(current)
      const result = newValue.length > 0 ? newValue : undefined
      onChangeRef.current(result)
      if (newValue.length === 0) {
        search()
        closePopup()
        onClose()
      }
    },
    [parentMap, search, closePopup, onClose]
  )

  if (valueNames.length === 0) return <></>
  return (
    <>
      <SearchItemLabel
        ref={ref}
        active={open}
        onClick={openPopup}
        onClear={onClear}
        isLoading={isLoading}
      >
        {intl.formatMessage({ id: 'wbsItemSearchFilterPanel.processScope' })}:{' '}
        {valueNames.join(', ')}
      </SearchItemLabel>
      {open && !isLoading && (
        <ProcessScopeSearchSelectPopper
          open={open && !isLoading}
          anchorEl={ref.current || undefined}
          options={options}
          parentMap={parentMap}
          isLoading={false}
          selectedValues={selectedValues}
          onToggleItem={handleToggleItem}
          onClose={onClosePopup}
          disablePortal={false}
        />
      )}
    </>
  )
}
